import { Button } from "@component/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@component/card";
import { ChartContainer } from "@component/chart";
import { Pagination } from "@component/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@component/table";
import { Tabs, TabsList, TabsTrigger } from "@component/tabs";
import { Tooltip } from "@component/tooltip";
import { useSubscription } from "@context/subscription_context";
import CurrentBillingPeriodCard from "@container/current-billing-period-card";
import { getCompanyApplications, getSubscriptionActivitySummary, getSubscriptionActivityTracking, SubscriptionDateRange } from "@service/subscription";
import { MetaPagination } from "@type/common";
import { ActivityTrackingEntry, SubscriptionActivityApplicantItem, SubscriptionActivitySummary } from "@type/subscription";
import { formatHumanReadable, parseFormatRaw } from "@util/date";
import { ClipboardListIcon, FileTextIcon, InfoIcon, RefreshCwIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Bar,
    BarChart,
    CartesianGrid,
    XAxis,
    YAxis,
    ResponsiveContainer,
    LabelList,
} from "recharts";

const LIMIT = 5;

interface BillingHiringActivityProps {
    /** When set (admin scope), scopes all fetches to this company. */
    companyId?: string;
    /** "workspace" (default) preserves billing page behaviour; "admin" allows companyId to be threaded through. */
    scope?: "workspace" | "admin";
    /** Admin-only date range. Sent to activity-summary / activity-tracking only
     *  in "admin" scope; the BE applies it only for admin/super-admin roles. */
    dateRange?: SubscriptionDateRange;
    /** Show the "Current Billing Period" card. Off when this is embedded under a
     *  surface that already renders the subscription/billing-period context
     *  (e.g. the Subscription page's Workspace Activity tab), to avoid duplication. */
    showBillingPeriod?: boolean;
}

export default function BillingHiringActivity({ companyId, scope = "workspace", dateRange, showBillingPeriod = true }: BillingHiringActivityProps = {}) {
    const { subscriptionActive } = useSubscription();
    const effectiveCompanyId = scope === "admin" ? companyId : undefined;
    // Date range is an admin-only filter — never send it in workspace scope.
    const effectiveDateRange = scope === "admin" ? dateRange : undefined;
    // Stable primitive dep so the fetch effects re-run when the range changes.
    const dateRangeKey = effectiveDateRange
        ? `${effectiveDateRange.start_date}|${effectiveDateRange.end_date}`
        : "";
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [usageTrackingPeriod, setUsageTrackingPeriod] = useState<"daily" | "weekly" | "monthly">("daily");
    const [isLoadingCVParsed, setIsLoadingCVParsed] = useState<boolean>(false);
    const [isLoadingAIAssessment, setIsLoadingAIAssessment] = useState<boolean>(false);
    const [currentPageCVParsed, setCurrentPageCVParsed] = useState<number>(1);
    const [currentPageAIAssessment, setCurrentPageAIAssessment] = useState<number>(1);
    const [cvTrackingData, setCvTrackingData] = useState<ActivityTrackingEntry[]>([]);
    const [aiTrackingData, setAiTrackingData] = useState<ActivityTrackingEntry[]>([]);
    const [activitySummary, setActivitySummary] = useState<SubscriptionActivitySummary>({
        // CVs Parsed
        total_cv_parsed: 0,
        cv_parsed_plan: 0,
        cv_parsed_overage: 0,
        // AI Assessments Sent
        total_ai_sent: 0,
        ai_assessment_sent_plan: 0,
        ai_assessment_sent_overage: 0,
        // AI Assessments Completed
        ai_assessment_completed_plan: 0,
        ai_assessment_completed_rate: 0,
        ai_assessment_pending_assessment: 0,
    });
    const [activityTrackingApplicantCVParseItems, setActivityTrackingApplicantCVParseItems] = useState<SubscriptionActivityApplicantItem[]>([]);
    const [activityTrackingApplicantAIAssessmentItems, setActivityTrackingApplicantAIAssessmentItems] = useState<SubscriptionActivityApplicantItem[]>([]);
    const [metaPaginationCVParsed, setMetaPaginationCVParsed] = useState<MetaPagination>({
        limit: 0,
        page: 1,
        total_pages: 0,
        total: 0,
    })
    const [metaPaginationAIAssessment, setMetaPaginationAIAssessment] = useState<MetaPagination>({
        limit: 0,
        page: 1,
        total_pages: 0,
        total: 0,
    })

    useEffect(() => {
        getSubscriptionActivitySummary(effectiveCompanyId, effectiveDateRange).then((resp) => {
            if (resp.status === 200) {
                setActivitySummary(resp.data.data);
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveCompanyId, dateRangeKey])

    useEffect(() => {
        getSubscriptionActivityTracking("cv_parsed", usageTrackingPeriod, effectiveCompanyId, effectiveDateRange).then((resp) => {
            if (resp.status === 200) {
                setCvTrackingData(resp.data.data);
            }
        })
        getSubscriptionActivityTracking("ai_assessment", usageTrackingPeriod, effectiveCompanyId, effectiveDateRange).then((resp) => {
            if (resp.status === 200) {
                setAiTrackingData(resp.data.data);
            }
        })

        getCompanyApplications(1, LIMIT, "cv_parsed", usageTrackingPeriod, effectiveCompanyId).then((resp) => {
            if (resp.status === 200) {
                setActivityTrackingApplicantCVParseItems(resp.data.data);
                setMetaPaginationCVParsed(resp.data.meta.pagination);
            }
        })
        getCompanyApplications(1, LIMIT, "ai_assessment", usageTrackingPeriod, effectiveCompanyId).then((resp) => {
            if (resp.status === 200) {
                setActivityTrackingApplicantAIAssessmentItems(resp.data.data);
                setMetaPaginationAIAssessment(resp.data.meta.pagination);
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usageTrackingPeriod, effectiveCompanyId, dateRangeKey])

    const handlePeriodChange = (value: "daily" | "weekly" | "monthly") => {
        setUsageTrackingPeriod(value);
        setCurrentPageAIAssessment(1);
        setCurrentPageCVParsed(1);
    };

    const onReloadSummary = () => {
        setIsRefreshing(true);
        getSubscriptionActivityTracking("cv_parsed", usageTrackingPeriod, effectiveCompanyId, effectiveDateRange).then((resp) => {
            if (resp.status === 200) {
                setCvTrackingData(resp.data.data);
            }
        })
        getSubscriptionActivityTracking("ai_assessment", usageTrackingPeriod, effectiveCompanyId, effectiveDateRange).then((resp) => {
            if (resp.status === 200) {
                setAiTrackingData(resp.data.data);
            }
        }).finally(() => {
            setIsRefreshing(false);
        })
    }

    const getUsageData = (type: "cv" | "ai") => {
        if (type === "cv") {
            return cvTrackingData.map((entry) => ({
                label: entry.label,
                "CVs Parsed": entry.count,
            }));
        }
        return aiTrackingData.map((entry) => ({
            label: entry.label,
            "AI Screening Invitations Sent": entry.count_pending,
            "AI Screenings Completed": entry.count_completed,
        }));
    }

    const onLoadPaginationCVParsed = (page: number) => {
        setCurrentPageCVParsed(page);
        setIsLoadingCVParsed(true);
        getCompanyApplications(page, LIMIT, "cv_parsed", usageTrackingPeriod, effectiveCompanyId).then((resp) => {
            if (resp.status === 200) {
                setActivityTrackingApplicantCVParseItems(resp.data.data);
                setMetaPaginationCVParsed(resp.data.meta.pagination);
            }
        }).finally(() => {
            setIsLoadingCVParsed(false);
        })
    }

    const onLoadPaginationAIAssessment = (page: number) => {
        setCurrentPageAIAssessment(page);
        setIsLoadingAIAssessment(true);
        getCompanyApplications(page, LIMIT, "ai_assessment", usageTrackingPeriod, effectiveCompanyId).then((resp) => {
            if (resp.status === 200) {
                setActivityTrackingApplicantAIAssessmentItems(resp.data.data);
                setMetaPaginationAIAssessment(resp.data.meta.pagination);
            }
        }).finally(() => {
            setIsLoadingAIAssessment(false);
        })
    }

    return (
        <div className="space-y-6">
            {/* Current Billing Period — hidden in admin scope (cross-company view has no single billing cycle)
                and when the host surface already shows the subscription context (showBillingPeriod=false). */}
            {scope !== "admin" && showBillingPeriod && <CurrentBillingPeriodCard />}

            {/* Activity Summary Section */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex flex-col gap-1">
                        <h3 className="text-lg font-semibold">Activity Summary</h3>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">
                                CVs Parsed
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 pt-2">
                            {/* Primary metric */}
                            <div>
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                                    Processed Per Billing Period
                                </p>
                                <span className="text-3xl font-bold">{activitySummary.total_cv_parsed}</span>
                            </div>
                            <ul className="m-0 p-0 flex flex-col gap-1">
                                <li className="flex items-start text-muted-foreground gap-2 text-sm m-0 p-0">
                                    <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                                    <span>Included in plan:</span>
                                    <span className="font-medium">{activitySummary.cv_parsed_plan}</span>
                                </li>
                                {!subscriptionActive?.is_trial && (
                                    <li className="flex items-start text-muted-foreground gap-2 text-sm m-0 p-0">
                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                                        <span>Additional processed:</span>
                                        <div className="flex items-center gap-1">
                                            <Tooltip
                                                content="Additional usage will be billed at the end of the billing cycle."
                                            >
                                                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                            </Tooltip>
                                            <span className="font-medium">{activitySummary.cv_parsed_overage}</span>
                                        </div>
                                    </li>
                                )}
                            </ul>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">
                                AI Screenings Completed
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 pt-2">
                            {/* Primary metric */}
                            <div>
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                                    Completed Per Billing Period
                                </p>
                                <div className="flex items-center gap-1">
                                    <span className="text-3xl font-bold">{activitySummary.ai_assessment_completed_plan}</span>
                                    <span className="text-gray-500">({activitySummary.total_ai_sent} sent, {activitySummary.ai_assessment_pending_assessment} pending)</span>
                                </div>

                            </div>

                            <ul className="m-0 p-0 flex flex-col gap-1">
                                <li className="flex items-start text-muted-foreground gap-2 text-sm m-0 p-0">
                                    <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                                    <span>Included in plan:</span>
                                    <span className="font-medium">{activitySummary.ai_assessment_sent_plan}</span>
                                </li>
                                {!subscriptionActive?.is_trial && (
                                    <li className="flex items-start text-muted-foreground gap-2 text-sm m-0 p-0">
                                        <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                                        <span>Additional completed:</span>
                                        <div className="flex items-center gap-1">
                                            <Tooltip
                                                content="Additional usage will be billed at the end of the billing cycle."
                                            >
                                                <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                            </Tooltip>
                                            <span className="font-medium">{activitySummary.ai_assessment_sent_overage}</span>
                                        </div>
                                    </li>
                                )}
                                <li className="flex items-start text-muted-foreground gap-2 text-sm m-0 p-0">
                                    <div className="h-1.5 w-1.5 rounded-full bg-foreground mt-1.5 flex-shrink-0" />
                                    <span>Completion rate:</span>
                                    <span className="font-medium">{activitySummary.ai_assessment_completed_rate.toFixed(0)}%</span>
                                </li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Activity Tracking Section */}
            <div>
                <div className="flex items-center justify-between mb-4 gap-4">
                    <div>
                        <h3 className="text-lg font-semibold">Activity Tracking</h3>
                        <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-muted-foreground">
                                Last updated: {activitySummary.last_updated ? formatHumanReadable(activitySummary.last_updated, true) : "N/A"}
                            </p>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 hover:bg-transparent"
                                onClick={onReloadSummary}
                                disabled={isRefreshing}
                            >
                                <RefreshCwIcon
                                    className={`h-3 w-3 text-muted-foreground hover:text-foreground transition-colors ${isRefreshing ? "animate-spin" : ""}`}
                                />
                            </Button>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <Tabs
                            value={usageTrackingPeriod}
                            onValueChange={(value) =>
                                handlePeriodChange(
                                    value as "daily" | "weekly" | "monthly"
                                )
                            }
                            className="w-auto"
                        >
                            <TabsList className="h-9">
                                <TabsTrigger
                                    value="daily"
                                    className="text-xs px-3 data-[state=active]:text-blue-600"
                                >
                                    Daily
                                </TabsTrigger>
                                <TabsTrigger
                                    value="weekly"
                                    className="text-xs px-3 data-[state=active]:text-blue-600"
                                >
                                    Weekly
                                </TabsTrigger>
                                <TabsTrigger
                                    value="monthly"
                                    className="text-xs px-3 data-[state=active]:text-blue-600"
                                >
                                    Monthly
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                        {/* <p className="text-xs text-muted-foreground">
                            xxx
                        </p> */}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* CVs Parsed Chart */}
                    <Card className="flex flex-col">
                        <CardHeader className="space-y-1">
                            <CardTitle className="text-base">CVs Parsed</CardTitle>
                            <CardDescription>
                                {usageTrackingPeriod === "daily" &&
                                    "Daily CV parsing activity"}
                                {usageTrackingPeriod === "weekly" &&
                                    "Weekly CV parsing activity"}
                                {usageTrackingPeriod === "monthly" &&
                                    "Monthly CV parsing activity"}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className={activityTrackingApplicantCVParseItems.length === 0 ? "flex-1 flex items-center justify-center" : "space-y-6"}>
                            {/* Empty State for CVs Parsed */}
                            {activityTrackingApplicantCVParseItems.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 px-6">
                                    <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                        <FileTextIcon className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                    <p className="text-center text-muted-foreground font-medium mb-2">
                                        No CVs parsed during this period.
                                    </p>
                                    <p className="text-center text-sm text-muted-foreground/80">
                                        CV parsing activity will appear here once candidates
                                        apply.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <ChartContainer
                                        config={{}}
                                        className="h-[300px] w-full"
                                    >
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={getUsageData("cv") as never[]} margin={{ top: 20 }}>
                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    className="stroke-muted"
                                                />

                                                <XAxis
                                                    dataKey="label"
                                                    className="text-xs"
                                                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                                                />

                                                <YAxis
                                                    className="text-xs"
                                                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                                                />

                                                <Bar
                                                    dataKey="CVs Parsed"
                                                    fill="hsl(210, 100%, 65%)"
                                                    radius={[4, 4, 0, 0]}
                                                >
                                                    <LabelList
                                                        dataKey="CVs Parsed"
                                                        position="top"
                                                        style={{
                                                            fill: "hsl(var(--foreground))",
                                                            fontSize: "12px",
                                                            fontWeight: "500",
                                                        }}
                                                    />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </ChartContainer>
                                    <div>
                                        <h4 className="text-sm font-semibold mb-3">
                                            Recent CVs Parsed
                                        </h4>
                                        <div className="border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="bg-muted/50">
                                                        <TableHead className="w-12 px-4">#</TableHead>
                                                        <TableHead className="px-6">
                                                            Candidate Details
                                                        </TableHead>
                                                        <TableHead className="px-6">
                                                            <div className="flex items-center gap-2">
                                                                Job Title
                                                                <Tooltip
                                                                    content="Click to navigate to the job listing."
                                                                >
                                                                    <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                                </Tooltip>
                                                            </div>
                                                        </TableHead>
                                                        <TableHead className="px-6">
                                                            Date Parsed
                                                        </TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {activityTrackingApplicantCVParseItems.map((item, index) => (
                                                        <TableRow key={item.application_id}>
                                                            <TableCell className="font-medium px-4">
                                                                {currentPageCVParsed === 1
                                                                    ? index + 1
                                                                    : (currentPageCVParsed - 1) * LIMIT + index + 1}
                                                            </TableCell>
                                                            <TableCell className="px-6">
                                                                <div className="flex flex-col">
                                                                    <span className="font-medium">
                                                                        {item.candidate_name}
                                                                    </span>
                                                                    <span className="text-xs text-muted-foreground">
                                                                        {item.candidate_email}
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="px-6">
                                                                <Link
                                                                    to={`/jobs/edit/${item.job_id}`}
                                                                    target="_blank"
                                                                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline transition-colors"
                                                                >
                                                                    <div className="max-w-xs break-words">
                                                                        {item.job_title}
                                                                    </div>
                                                                </Link>
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground px-6">
                                                                {parseFormatRaw(item.date || "", "D MMM YYYY")}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                        <div>
                                            <Pagination
                                                isLoading={isLoadingCVParsed}
                                                currentPage={currentPageCVParsed}
                                                totalPages={metaPaginationCVParsed.total_pages || 0}
                                                onPageChange={onLoadPaginationCVParsed}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="flex flex-col">
                        <CardHeader className="space-y-1">
                            <div className="flex items-start justify-between">
                                <div className="space-y-1">
                                    <CardTitle className="text-base">
                                        AI Screenings
                                    </CardTitle>
                                    <CardDescription>
                                        {usageTrackingPeriod === "daily" &&
                                            "Daily screening activity"}
                                        {usageTrackingPeriod === "weekly" &&
                                            "Weekly screening activity"}
                                        {usageTrackingPeriod === "monthly" &&
                                            "Monthly screening activity"}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="h-3 w-3 rounded"
                                            style={{
                                                backgroundColor: "#B3D8FF",
                                            }}
                                        />
                                        <span className="text-xs text-muted-foreground">
                                            Sent
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="h-3 w-3 rounded"
                                            style={{
                                                backgroundColor: "#2B73E6",
                                            }}
                                        />
                                        <span className="text-xs text-muted-foreground">
                                            Completed
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {(aiTrackingData.length > 0) && (
                            <ChartContainer
                                config={{}}
                                className="h-[300px] w-full"
                            >
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={getUsageData("ai") as never[]}
                                        barGap={0}
                                        barCategoryGap="20%"
                                        margin={{ top: 20 }}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            className="stroke-muted"
                                        />

                                        <XAxis
                                            dataKey="label"
                                            className="text-xs"
                                            tick={{ fill: "hsl(var(--muted-foreground))" }}
                                        />

                                        <YAxis
                                            className="text-xs"
                                            tick={{ fill: "hsl(var(--muted-foreground))" }}
                                        />

                                        <Bar
                                            dataKey="AI Screening Invitations Sent"
                                            fill="#B3D8FF"
                                            radius={[4, 4, 0, 0]}
                                        >
                                            <LabelList
                                                dataKey="AI Screening Invitations Sent"
                                                position="top"
                                                style={{
                                                    fill: "hsl(var(--foreground))",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                            />
                                        </Bar>

                                        <Bar
                                            dataKey="AI Screenings Completed"
                                            fill="#2B73E6"
                                            radius={[4, 4, 0, 0]}
                                        >
                                            <LabelList
                                                dataKey="AI Screenings Completed"
                                                position="top"
                                                style={{
                                                    fill: "hsl(var(--foreground))",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartContainer>
                            )}
                            {/* Empty State for AI Assessments */}
                            {activityTrackingApplicantAIAssessmentItems.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 px-6">
                                    <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                        <ClipboardListIcon className="h-8 w-8 text-muted-foreground" />
                                    </div>
                                    <p className="text-center text-muted-foreground font-medium mb-2">
                                        No screenings completed during this period.
                                    </p>
                                    <p className="text-center text-sm text-muted-foreground/80">
                                        Screening activity will appear here once candidates
                                        complete their screenings.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <h4 className="text-sm font-semibold mb-3">
                                            Recent AI Screenings Completed
                                        </h4>
                                        <div className="border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="bg-muted/50">
                                                        <TableHead className="w-12 px-4">#</TableHead>
                                                        <TableHead className="px-6">
                                                            Candidate Details
                                                        </TableHead>
                                                        <TableHead className="px-6">
                                                            <div className="flex items-center gap-2">
                                                                Job Title
                                                                <Tooltip
                                                                    content="Click to navigate to the job listing."
                                                                >
                                                                    <InfoIcon className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                                                </Tooltip>
                                                            </div>
                                                        </TableHead>
                                                        <TableHead className="px-6">
                                                            Date Completed
                                                        </TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {activityTrackingApplicantAIAssessmentItems.map((item, index) => (
                                                        <TableRow key={item.application_id}>
                                                            <TableCell className="font-medium px-4">
                                                                {currentPageAIAssessment === 1
                                                                    ? index + 1
                                                                    : (currentPageAIAssessment - 1) * LIMIT + index + 1}
                                                            </TableCell>
                                                            <TableCell className="px-6">
                                                                <div className="flex flex-col">
                                                                    <span className="font-medium">
                                                                        {item.candidate_name}
                                                                    </span>
                                                                    <span className="text-xs text-muted-foreground">
                                                                        {item.candidate_email}
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="px-6">
                                                                <Link
                                                                    to={`/jobs/edit/${item.job_id}`}
                                                                    target="_blank"
                                                                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline transition-colors"
                                                                >
                                                                    <div className="max-w-xs break-words">
                                                                        {item.job_title}
                                                                    </div>
                                                                </Link>
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground px-6">
                                                                {parseFormatRaw(item.date || "", "D MMM YYYY")}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                        <div>
                                            <Pagination
                                                isLoading={isLoadingAIAssessment}
                                                currentPage={currentPageAIAssessment}
                                                totalPages={metaPaginationAIAssessment.total_pages || 0}
                                                onPageChange={onLoadPaginationAIAssessment}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
